import { FC, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import type { Container, BatteryType, Battery } from '@/hooks/useBatteryContainers';

interface AddBatteryModalProps {
  open: boolean;
  onClose: () => void;
  container: Container;
  batteryTypes: BatteryType[];
  existingBatteries: Battery[];
  onAdd: (batteryTypeId: string, quantity: number) => Promise<void>;
}

export const AddBatteryModal: FC<AddBatteryModalProps> = ({
  open,
  onClose,
  container,
  batteryTypes,
  existingBatteries,
  onAdd,
}) => {
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [loading, setLoading] = useState(false);

  const handleAdd = async () => {
    if (!selectedTypeId) {
      toast.error('Selecteer een batterijtype');
      return;
    }

    const qty = parseInt(quantity);
    if (!qty || qty < 1) {
      toast.error('Aantal moet minimaal 1 zijn');
      return;
    }

    const selectedType = batteryTypes.find((t) => t.id === selectedTypeId);
    if (!selectedType) return;

    // Check capacity
    const containerBatteries = existingBatteries.filter((b) => b.container_id === container.id);
    const current50kw = containerBatteries.filter((b) => b.capacity_kwh === 50).length;
    const current100kw = containerBatteries.filter((b) => b.capacity_kwh === 100).length;

    const max50kw = container.max_batteries_50kw || 16;
    const max100kw = container.max_batteries_100kw || 8;

    if (selectedType.capacity_kwh === 50) {
      if (current50kw + qty > max50kw) {
        toast.error(`Niet genoeg ruimte. Container heeft plek voor ${max50kw - current50kw} x 50kW batterijen.`);
        return;
      }
    } else {
      if (current100kw + qty > max100kw) {
        toast.error(`Niet genoeg ruimte. Container heeft plek voor ${max100kw - current100kw} x 100kW batterijen.`);
        return;
      }
    }

    setLoading(true);
    try {
      await onAdd(selectedTypeId, qty);
      toast.success(`${qty} batterij(en) toegevoegd`);
      handleClose();
    } catch (error) {
      console.error('Error adding batteries:', error);
      toast.error('Fout bij toevoegen batterijen');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedTypeId('');
    setQuantity('1');
    setLoading(false);
    onClose();
  };

  const selectedType = batteryTypes.find((t) => t.id === selectedTypeId);
  const containerBatteries = existingBatteries.filter((b) => b.container_id === container.id);
  const current50kw = containerBatteries.filter((b) => b.capacity_kwh === 50).length;
  const current100kw = containerBatteries.filter((b) => b.capacity_kwh === 100).length;
  
  const max50kw = container.max_batteries_50kw || 16;
  const max100kw = container.max_batteries_100kw || 8;

  const maxQuantity = selectedType
    ? selectedType.capacity_kwh === 50
      ? max50kw - current50kw
      : max100kw - current100kw
    : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Batterijen Toevoegen</DialogTitle>
          <DialogDescription>
            Voeg batterijen toe aan {container.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Batterijtype</Label>
            <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer type" />
              </SelectTrigger>
              <SelectContent>
                {batteryTypes.map((type) => {
                  const available = type.capacity_kwh === 50 
                    ? max50kw - current50kw 
                    : max100kw - current100kw;
                  
                  return (
                    <SelectItem key={type.id} value={type.id} disabled={available <= 0}>
                      {type.name} ({type.capacity_kwh}kWh / {type.power_kw}kW)
                      {available > 0 ? ` - ${available} beschikbaar` : ' - Vol'}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {selectedType && (
            <div>
              <Label>Aantal</Label>
              <Input
                type="number"
                min="1"
                max={maxQuantity}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Nog {maxQuantity} x {selectedType.capacity_kwh}kW beschikbaar in deze container
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Annuleren
          </Button>
          <Button onClick={handleAdd} disabled={loading || maxQuantity <= 0}>
            {loading ? 'Toevoegen...' : 'Toevoegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
